import { Component, OnInit, signal, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgxPaginationModule } from 'ngx-pagination';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';

import { PageSize } from 'src/app/enumeration/pageSize';
import { RedeemApiService } from 'src/app/service/redeem/redeem-api.service';
import { DateFormatPipe } from 'src/app/utils/date-format.pipe';
import { DownloadService } from 'src/app/utils/download/download.service';

@Component({
  selector: 'app-redeem-download-list',
  standalone: true,
  imports: [
    CommonModule,
    NgxPaginationModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    DateFormatPipe,
  ],
  templateUrl: './redeem-download-list.component.html',
  styleUrl: './redeem-download-list.component.css',
})
export class RedeemDownloadListComponent implements OnInit {
  // ─── Modern Functional Injection Engine ───
  private readonly apiService = inject(RedeemApiService);
  private readonly downloadService = inject(DownloadService);
  private readonly destroyRef = inject(DestroyRef);

  // ─── Fine-Grained Reactive Signals Architecture ───
  readonly loading = signal<boolean>(false);
  readonly page = signal<number>(1);
  readonly pageSize = signal<number>(PageSize.REDEEM_LISTS);
  readonly allTotal = signal<number>(0);
  
  readonly dataList = signal<any[]>([]);
  readonly isDownloadBtnLoading = signal<boolean[]>([]);

  ngOnInit(): void {
    this.getAllList();
  }

  /**
   * Retrieves paginated downloadable dataset CSV log entries
   */
  getAllList(): void {
    let currentPage = this.page() -1 ;
    this.loading.set(true);
    this.apiService.getDownloadCSVList(currentPage)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: (data) => {
          this.dataList.set(data?.downloadRedeemRespLst || []);
          this.allTotal.set(data?.allTotal || 0);
        },
        error: (error) => {
          if (error) alert(error);
        }
      });
  }

  /**
   * Streams targeted export payload files to local client filesystems
   */
  downloadFile(fileName: string, index: number): void {
    if (!fileName) return;

    this.toggleButtonLoading(index, true);

    this.apiService.getDownloadFile(fileName)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.toggleButtonLoading(index, false))
      )
      .subscribe({
        next: (data) => {
          this.downloadService.downloadCsvWithModifiedTime(data, fileName);
        },
        error: (error) => {
          if (error) alert(error);
        }
      });
  }

  /**
   * Dynamic signal update utility method for granular tracking array modifications
   */
  private toggleButtonLoading(index: number, state: boolean): void {
    this.isDownloadBtnLoading.update((states) => {
      states[index] = state;
      return [...states];
    });
  }

  /**
   * Syncs interface page transitions over to the signal network tracker
   */
  handlePageChange(event: number): void {
    if (!this.loading()) {
      this.page.set(event);
      this.getAllList();
    }
  }

  /**
   * Browser-level clean native backward route jump tracking
   */
  goBack(): void {
    window.history.back();
  }
}
